import React, { useState, useEffect } from "react";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import {
  getSuspendedCertificatesRequest,
  setCertificateFilterProductionPeriod,
  setCertificateFilterExportStartDate,
  setCertificateFilterExportEndDate,
  setCertificateFilterResourceType,
  setCertificateFilterStation,
} from "../../../redux/actions/certificates";
import { convertAmountToFrontend } from "../../../helpers/convert";
import Button from "../../../components/Button";
import ComboBox from "../../../components/ComboBox";
import { Input, Select } from "../../../components/Inputs";
import Pill from "../../../components/Pill";
import EmptyState from "../../../components/EmptyState/EmptyState";
import Loading from "../../../components/Loading";
import ModalCertificate from "../../../components/Modal/ModalCertificate";
import ComboBoxStation from "../../../components/ComboBoxStation";
import { API_EXPORT } from "../../../helpers/api";
import { monthsAndResourceTypes } from "../../../constants/translations";
import { resourceTypes } from "../../../constants/selects";
import fileDownload from "js-file-download";
import { setNotification } from "../../../helpers/notification";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";

export default function SuspendedCertificates({ t }) {
  const dispatch = useDispatch();
  const { suspendedList: certificates, total } = useSelector(
    (state) => state.certificates
  );

  const loading = useSelector((state) => state.certificates.loading);
  const selectedOrganization = useSelector(
    (state) => state.organizations.selectedOrganization
  );
  // Filters
  const filterStation = useSelector(
    (state) => state.certificates.filterStation
  );
  const filterResourceType = useSelector(
    (state) => state.certificates.filterResourceType
  );
  const filterProductionPeriodMonth = useSelector(
    (state) => state.certificates.filterProductionPeriodMonth
  );
  const filterProductionPeriodYear = useSelector(
    (state) => state.certificates.filterProductionPeriodYear
  );
  const filterExportStartDateMonth = useSelector(
    (state) => state.certificates.filterExportStartDateMonth
  );
  const filterExportStartDateYear = useSelector(
    (state) => state.certificates.filterExportStartDateYear
  );
  const filterExportEndDateMonth = useSelector(
    (state) => state.certificates.filterExportEndDateMonth
  );
  const filterExportEndDateYear = useSelector(
    (state) => state.certificates.filterExportEndDateYear
  );

  const documentCount = useSelector(
    (state) => state.certificates.documentCount
  );

  const [inspectedCertificateId, setInspectedCertificateId] = useState();
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadFilter, setDownloadFilter] = useState({
    organizationId: "",
    stationId: "",
    resourceType: "",
    month: "",
    year: "",
    certificateStatus: ["REPEAL"],
  });
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activePage, setActivePage] = useState(0);
  const translations = monthsAndResourceTypes(t);
  const selectResourceType = resourceTypes(t);

  const suspendedCertificatesColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "productionPeriod",
      sortable: true,
      cell: (row) => {
        return `${translations[row.productionMonth]}/${row.productionYear}`;
      },
    },
    {
      name: t("pages.default.certificates.table.exportPeriod"),
      selector: "exportDate",
      sortable: true,
      cell: (row) =>
        moment(row.exportDate).isValid()
          ? moment(row.exportDate).format("DD/MM/YYYY HH:mm:ss")
          : "-",
    },
    {
      name: t("pages.default.certificates.table.documentCount"),
      selector: "amount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: t("pages.default.certificates.table.status"),
      selector: "certificateStatus",
      sortable: true,
      cell: (row) => {
        return (
          <Pill label={translations[row.certificateStatus]} type="attention" />
        );
      },
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      cell: (row) => {
        return (
          <Button
            title={t("ui.default.review")}
            suffixIcon="search"
            type="secondary"
            onClick={() => inspectCertificate(row)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    filterCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterChange = (e) => {
    let splitted = [];
    let payload = {};
    splitted = e.target.value.split("-");
    payload = {
      month: splitted[1] || "",
      year: splitted[0] || "",
    };
    switch (e.target.name) {
      case "exportStartDate":
        dispatch(setCertificateFilterExportStartDate(payload));
        break;
      case "exportEndDate":
        dispatch(setCertificateFilterExportEndDate(payload));
        break;
      case "productionPeriod":
        dispatch(setCertificateFilterProductionPeriod(payload));
        break;
      case "resourceType":
        dispatch(setCertificateFilterResourceType(e.target.value));
        break;
      default:
        break;
    }
  };
  const selectStation = (station) => {
    dispatch(setCertificateFilterStation(station));
  };

  const inspectCertificate = (row) => {
    setInspectedCertificateId(row.id);
  };

  const filterCertificates = () => {
    let params = {
      organizationId:
        selectedOrganization && selectedOrganization.organizationId
          ? selectedOrganization.organizationId
          : "",
      stationId: filterStation ? filterStation.stationId : "",
      resourceType: filterResourceType !== "-" ? filterResourceType : "",
      month:
        filterProductionPeriodMonth !== "-" ? filterProductionPeriodMonth : "",
      year:
        filterProductionPeriodYear !== "-" ? filterProductionPeriodYear : "",
      exportStartDateMonth: filterExportStartDateMonth,
      exportEndDateMonth: filterExportEndDateMonth,
      exportStartDateYear: filterExportStartDateYear,
      exportEndDateYear: filterExportEndDateYear,
      certificateStatus: ["REPEAL"],
    };
    dispatch(
      getSuspendedCertificatesRequest({
        ...params,
      })
    );
    setDownloadFilter({
      ...params,
      lang: window.localStorage.i18nextLng,
    });
  };

  const downloadCertificates = async () => {
    setExportLoading(true);
    try {
      const res = await API_EXPORT("certificate/export/excel", {
        ...downloadFilter,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };

  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    let params = {
      organizationId:
        selectedOrganization && selectedOrganization.organizationId
          ? selectedOrganization.organizationId
          : "",
      stationId: filterStation ? filterStation.stationId : "",
      resourceType: filterResourceType !== "-" ? filterResourceType : "",
      month:
        filterProductionPeriodMonth !== "-" ? filterProductionPeriodMonth : "",
      year:
        filterProductionPeriodYear !== "-" ? filterProductionPeriodYear : "",
      exportStartDateMonth: filterExportStartDateMonth,
      exportEndDateMonth: filterExportEndDateMonth,
      exportStartDateYear: filterExportStartDateYear,
      exportEndDateYear: filterExportEndDateYear,
      certificateStatus: ["REPEAL"],
      page: activePage,
      selector: selector,
      direction: direction,
    };
    dispatch(
      getSuspendedCertificatesRequest({
        ...params,
      })
    );
  };

  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.certificates.suspendedTitle")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--certificates-filter">
          <ComboBox t={t} />
          <ComboBoxStation
            t={t}
            selectedStation={filterStation}
            selectStation={selectStation}
          />
          <Input
            type="month"
            placeholder={t(
              "pages.default.certificates.filters.allProductionPeriod"
            )}
            name="productionPeriod"
            value={
              filterProductionPeriodYear
                ? `${filterProductionPeriodYear}-${filterProductionPeriodMonth}`
                : ""
            }
            onChange={filterChange}
          />
          <Input
            type="month"
            placeholder={t(
              "pages.default.certificates.filters.exportStartDate"
            )}
            name="exportStartDate"
            placement="bottom-end"
            value={
              filterExportStartDateYear
                ? `${filterExportStartDateYear}-${filterExportStartDateMonth}`
                : ""
            }
            onChange={filterChange}
          />
          <Select
            options={selectResourceType}
            name="resourceType"
            defaultValue={filterResourceType}
            onChange={filterChange}
          />
          <Button
            type="primary"
            label={t("ui.default.filter")}
            onClick={filterCertificates}
            id="c-filter"
          />
          <Button
            type="secondary"
            label={t("ui.default.download")}
            suffixIcon="download"
            onClick={downloadCertificates}
            disabled={exportLoading}
            id="c-download"
          />
        </div>
        {certificates.length > 0 && !loading && (
          // <DataTable
          //   rowKey="id"
          //   columns={suspendedCertificatesColumns}
          //   data={certificates}
          //   subTotals={[
          //     {
          //       totalCount: documentCount.totalCount,
          //       countInPage: documentCount.countInPage,
          //       sumIndex: 5,
          //     },
          //   ]}
          //   defaultSortedField={"productionPeriod"}
          //   defaultSortAsc={false}
          //   paginationPerPage={15}
          //   pagination
          // />
          <DataTableServerSideRendering
            columns={suspendedCertificatesColumns}
            data={certificates}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={total?.totalPageCount}
            handleFetchData={handleClick}
            subTotals={[
              {
                totalCount: documentCount.totalCount,
                countInPage: documentCount.countInPage,
                sumIndex: 5,
              },
            ]}
          />
        )}
        {certificates.length === 0 && !loading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.certificates.suspendedNotFound")}
          />
        )}
        {loading && (
          <Loading text={t("pages.default.certificates.suspendedLoading")} />
        )}
      </div>
      {inspectedCertificateId && (
        <ModalCertificate
          t={t}
          certificateId={inspectedCertificateId}
          isOpen={inspectedCertificateId ? true : false}
          toggle={() => setInspectedCertificateId()}
          difCalculate={true}
        />
      )}
    </>
  );
}
